import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  calcCurrentPageWithNum,
  calcIndexByOrder,
  removeFalsyKeys,
} from 'utils/common';
import { useCustomSearchParams } from 'hooks/useCustomSearchParams';
import { StyledActions, StyledButton } from 'styles/overrides';
import { Table } from 'components/table';
import { useBranches, useDeleteBranches } from 'api/branchApi';
import { Notification } from 'components/Notification';
import { formatTime } from 'utils/timeUtils';
import BranchFilterSearch from '../BranchFilterSearch/BranchFilterSearch';
import { Modal } from 'antd';
import { Link } from 'react-router-dom';
import { PATH_NAME_ACTION } from 'constants/routes';
import { formatAddress } from 'utils/formatUtils';
import { Permission } from 'components/permission/Permission';
import { ROLE } from 'constants/roles';

export default function BranchTable() {
  const intl = useIntl();
  const [search, setSearch] = useCustomSearchParams();
  const { data, isLoading, refetch } = useBranches({ params: search });
  const deleteUserMutation = useDeleteBranches();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const columns = [
    {
      title: intl.formatMessage({ id: 'common.table.no' }),
      render: (text, record, index) => {
        return calcIndexByOrder({
          index,
          page: search.page,
          limit: search.page_size,
        });
      },
    },
    {
      title: intl.formatMessage({ id: 'views.branches.table.name' }),
      dataIndex: ['name'],
    },
    {
      title: intl.formatMessage({ id: 'views.branches.table.code' }),
      dataIndex: ['code'],
    },
    {
      title: intl.formatMessage({ id: 'views.branches.table.address' }),
      render: ({ address, ward, district, province }) =>
        formatAddress({
          address,
          ward: ward?.name,
          district: district?.name,
          province: province?.name,
        }),
    },
    {
      title: intl.formatMessage({ id: 'common.table.createdAt' }),
      dataIndex: 'created_at',
      render: (value) => formatTime(value),
    },
    {
      render: ({ id }) => (
        <Link to={`${id}`}>
          <StyledButton type="primary">
            {intl.formatMessage({ id: 'common.btn.seeDetails' })}
          </StyledButton>
        </Link>
      ),
    },
  ];

  const handleDelete = () => {
    if (selectedRowKeys.length <= 0) return;

    Modal.confirm({
      title: intl.formatMessage({ id: 'common.modal.deleteTitle' }),
      okText: intl.formatMessage({ id: 'common.btn.confirm' }),
      onOk() {
        return deleteUserMutation.mutateAsync(selectedRowKeys, {
          onSuccess() {
            const calculatedPage = calcCurrentPageWithNum({
              page: search.page,
              limit: search.page_size,
              total: data?.meta?.total,
              num: selectedRowKeys.length,
            });

            if (calculatedPage !== search.page) {
              setSearch(
                removeFalsyKeys({
                  ...search,
                  page: calculatedPage,
                }),
              );
            } else {
              refetch();
            }

            setSelectedRowKeys([]);
            Notification(
              'success',
              intl.formatMessage({ id: 'message.deleteSuccess' }),
            );
          },
          onError(error) {
            Notification(
              'error',
              error?.message ||
                intl.formatMessage({ id: 'message.commonError' }),
            );
          },
        });
      },
      onCancel() {},
    });
  };

  const handleChangeTable = ({ current: page, pageSize: limit }) => {
    setSearch(removeFalsyKeys({ ...search, page, page_size: limit }));
  };

  return (
    <>
      <BranchFilterSearch
        onSubmit={(values) => setSearch(values)}
        initialValues={search}
      />

      <StyledActions size={24}>
        <Link to={PATH_NAME_ACTION.CREATE}>
          <StyledButton type="primary">
            {intl.formatMessage({ id: 'common.btn.addNew' })}
          </StyledButton>
        </Link>

        <Permission roles={[ROLE.ADMIN]}>
          <StyledButton
            onClick={handleDelete}
            disabled={selectedRowKeys.length === 0}
          >
            {intl.formatMessage({ id: 'common.btn.delete' })}
          </StyledButton>
        </Permission>
      </StyledActions>

      <Table
        rowKey="id"
        rowSelection={{
          selectedRowKeys,
          onChange: (newSelectedRowKeys) => {
            setSelectedRowKeys(newSelectedRowKeys);
          },
          preserveSelectedRowKeys: true,
        }}
        columns={columns}
        dataSource={data?.data || []}
        loading={isLoading || deleteUserMutation.isLoading}
        pagination={{
          current: search?.page,
          pageSize: search?.page_size,
          total: data?.total || 0,
        }}
        onChange={handleChangeTable}
      />
    </>
  );
}
